"use client";

import {
	exitMarketingVideoFullscreen,
	resolveMarketingVideoElement,
} from "@marketing/shared/lib/marketing-landing-video-fullscreen";
import { safeExitDocumentFullscreen } from "@marketing/shared/lib/safe-document-fullscreen";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

interface MarketingLandingVideoOverlayContextValue {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	muted: boolean;
	setMuted: (next: boolean | ((prev: boolean) => boolean)) => void;
	hasUserUnmutedOnce: boolean;
	isFullscreen: boolean;
	setChromeFullscreen: (next: boolean) => void;
	toggleFullscreen: () => void;
	fullscreenTargetRef: React.RefObject<HTMLDivElement | null>;
	registerRequestClose: (fn: () => void) => () => void;
	requestClose: () => void;
}

const MarketingLandingVideoOverlayContext =
	createContext<MarketingLandingVideoOverlayContextValue | null>(null);

function applyMutedToTargetVideo(
	shell: HTMLDivElement | null,
	muted: boolean,
): void {
	const video = resolveMarketingVideoElement(shell);
	if (video == null) {
		return;
	}
	video.muted = muted;
}

export function MarketingLandingVideoOverlayProvider({
	children,
}: {
	children: ReactNode;
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [muted, setMutedState] = useState(true);
	const [hasUserUnmutedOnce, setHasUserUnmutedOnce] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const fullscreenTargetRef = useRef<HTMLDivElement>(null);
	const requestCloseRef = useRef<(() => void) | null>(null);

	const setMuted = useCallback(
		(next: boolean | ((prev: boolean) => boolean)) => {
			setMutedState((prev) => {
				const resolved = typeof next === "function" ? next(prev) : next;
				if (prev === true && resolved === false) {
					setHasUserUnmutedOnce(true);
				}
				applyMutedToTargetVideo(fullscreenTargetRef.current, resolved);
				return resolved;
			});
		},
		[],
	);

	useEffect(() => {
		if (!isOpen) {
			setMutedState(true);
			setHasUserUnmutedOnce(false);
			setIsFullscreen(false);
		}
	}, [isOpen]);

	const exitNativeFullscreen = useCallback(async () => {
		const shell = fullscreenTargetRef.current;
		if (shell != null) {
			await exitMarketingVideoFullscreen(shell);
		}
		await safeExitDocumentFullscreen();
	}, []);

	const setChromeFullscreen = useCallback((next: boolean) => {
		setIsFullscreen(next);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			void exitNativeFullscreen();
		}
	}, [exitNativeFullscreen, isOpen]);

	const toggleFullscreen = useCallback(() => {
		setIsFullscreen((current) => !current);
	}, []);

	const registerRequestClose = useCallback((fn: () => void) => {
		requestCloseRef.current = fn;
		return () => {
			requestCloseRef.current = null;
		};
	}, []);

	const requestClose = useCallback(() => {
		requestCloseRef.current?.();
	}, []);

	const value = useMemo(
		() =>
			({
				isOpen,
				setIsOpen,
				muted,
				setMuted,
				hasUserUnmutedOnce,
				isFullscreen,
				setChromeFullscreen,
				toggleFullscreen,
				fullscreenTargetRef,
				registerRequestClose,
				requestClose,
			}) satisfies MarketingLandingVideoOverlayContextValue,
		[
			isOpen,
			muted,
			hasUserUnmutedOnce,
			isFullscreen,
			setChromeFullscreen,
			toggleFullscreen,
			registerRequestClose,
			requestClose,
			setMuted,
		],
	);

	return (
		<MarketingLandingVideoOverlayContext.Provider value={value}>
			{children}
		</MarketingLandingVideoOverlayContext.Provider>
	);
}

export function useMarketingLandingVideoOverlay(): MarketingLandingVideoOverlayContextValue {
	const ctx = useContext(MarketingLandingVideoOverlayContext);
	if (ctx == null) {
		throw new Error(
			"useMarketingLandingVideoOverlay must be used within MarketingLandingVideoOverlayProvider",
		);
	}
	return ctx;
}
